import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { BulkAction } from 'src/app/shared/interfaces'

import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core'
import { DataTable } from '@pascalhonegger/ng-datatable'

import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { Observable, Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  take,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Organization } from 'src/app/features/organization/organization'
import { organizationSelectors } from 'src/app/features/organization/organization.selectors'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { Constants } from 'src/app/shared/constants'
import { SerieProposal } from '../../serie-proposal'
import { serieProposalSelectors } from '../../serie-proposal.selectors'
import DEBOUNCED_TIME = Constants.DEBOUNCED_TIME

import moment from 'moment-mini'
import { Booking } from 'src/app/features/booking/booking'
import { Member } from 'src/app/features/member/member'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import {
  SerieProposalItem,
  SerieProposalItemStatus
} from '../serie-proposal-item'
import { serieProposalItemActions } from '../serie-proposal-item.actions'
import { SerieProposalItemService } from '../serie-proposal-item.service'
import { BsModalService } from 'ngx-bootstrap/modal'
import { SerieProposalItemEditModal } from '../serie-proposal-item-create-edit-modal/show.component'

@UntilDestroy()
@Component({
  selector: 'serie-proposal-item-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class SerieProposalItemTableComponent {
  @Input() tableData: SerieProposalItem[]
  @Input() bulkActions: BulkAction[]
  @Input() isLoading: boolean
  @Input() showStatus: boolean = true
  @Input() serieProposal: SerieProposal
  @Input() removeSearchTerm$: Observable<boolean>
  @Input() total: number
  @Input() showTotal = false
  @Input() pageCount: number
  @Input() activePage: number = 1
  @Input() limitPage: number = 10
  @Input() refreshData$: Observable<boolean>
  @Input() intersectingBookings: Booking[]
  @Input() showSearchField: boolean = true
  @Input() showEditButton: boolean = false
  @Output() onBulkAction = new EventEmitter<any>()
  @Output() onClickRow = new EventEmitter<any>()
  @Output() onSelectedPage = new EventEmitter<any>()
  @Output() onSelectLimit = new EventEmitter<any>()
  @Output() onSearchTerm = new EventEmitter<any>()
  @Output() onSortedTable = new EventEmitter<any>()

  @ViewChild('dataTable', { static: true }) dataTable: DataTable

  public filterTerm: string = ''
  public allCheckedToggle: boolean = false
  public checkedIds: Map<number, boolean> = new Map()
  public sortingField = 'startDate_asc'
  public selectedLanguage$: Observable<string>
  public searchTerm$ = new Subject<string>()
  public accessRights = AccessRights
  public productEntities$: Observable<Dictionary<Product>>
  public organizationEntities$: Observable<Dictionary<Organization>>
  public serieProposalEntities$: Observable<Dictionary<SerieProposal>>
  public memberEntities$: Observable<Dictionary<Member>>
  public serieProposalItemStatus = SerieProposalItemStatus
  public mfPages = 1
  public mfRowsOnPage = 25

  constructor(
    private store: Store<AppState>,
    private serieProposalItemService: SerieProposalItemService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.refreshData$?.pipe(untilDestroyed(this)).subscribe((refresh) => {
      if (refresh) {
        this.dataTable.inputData = this.tableData
      }
    })

    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.organizationEntities$ = this.store.select(
      organizationSelectors.selectEntities
    )
    this.serieProposalEntities$ = this.store.select(
      serieProposalSelectors.selectEntities
    )
    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)

    this.removeSearchTerm$?.pipe(untilDestroyed(this)).subscribe((remove) => {
      if (remove) {
        this.filterTerm = ''
      }
    })
    this.searchTerm$
      .pipe(
        distinctUntilChanged(),
        debounceTime(DEBOUNCED_TIME),
        untilDestroyed(this),
        filter((s) => s.length > 2)
      )
      .subscribe((term) => {
        this.store.dispatch(
          serieProposalItemActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                'linkedProduct.description': {
                  [CondOperator.CONTAINS_LOW]: term
                }
              },
              join: [
                { field: 'serieProposal' },
                { field: 'linkedProduct' },
                { field: 'linkedProduct.translations' }
              ]
            })
          })
        )
      })
  }

  public onRowClicked(row: SerieProposal) {
    this.onClickRow.emit(row)
  }

  public onCheckRow(targetRow: SerieProposal) {
    this.checkedIds.set(
      targetRow.id,
      !Boolean(this.checkedIds.get(targetRow.id))
    )
  }

  public onToggleAllChecked() {
    this.allCheckedToggle = !this.allCheckedToggle

    this.dataTable.data.forEach((row) =>
      this.checkedIds.set(row.id, this.allCheckedToggle)
    )
  }

  public onClickBulkAction(bulkOption: string) {
    const rows: SerieProposalItem[] = this.dataTable.data.filter((x) =>
      this.checkedIds.get(x.id)
    )

    if (rows.length > 0) {
      this.onBulkAction.emit({ action: bulkOption, items: rows })
    }

    this.allCheckedToggle = false
    this.dataTable.data.forEach((row) => this.checkedIds.set(row.id, false))
  }

  public selectPage(event) {
    this.onSelectedPage.emit(event)
  }

  public onSearch(value) {
    this.onSearchTerm.emit(value)
  }

  public setSearchLimit(event) {
    this.onSelectLimit.emit(event)
  }

  public onSortfieldClick(fieldName: string) {
    this.sortingField =
      this.sortingField == `${fieldName}_asc`
        ? `${fieldName}_desc`
        : `${fieldName}_asc`

    this.onSortedTable.emit(this.sortingField)
  }

  public hasConflictingBooking(serieProposalItem: SerieProposalItem): boolean {
    return this.serieProposalItemService.hasConflictingBooking(
      serieProposalItem,
      this.intersectingBookings
    )
  }

  public roundedMinute(initialDate) {
    const roundedDate = new Date(initialDate)
    return new Date(roundedDate).getMinutes() == 59 ||
      new Date(roundedDate).getMinutes() == 29
      ? moment(new Date(roundedDate)).add(1, 'minutes').toDate()
      : new Date(roundedDate)
  }

  public onClickMakeCounterProposal(serieProposalItem: SerieProposalItem) {
    this.store
      .select(memberSelectors.selectEntities)
      .pipe(
        take(1),
        withLatestFrom(
          this.store.select(
            serieProposalSelectors.selectById,
            serieProposalItem.serieProposalId
          )
        )
      )
      .subscribe(([memberEntities, serieProposal]) => {
        const modalRef = this.modalService.show(SerieProposalItemEditModal, {
          initialState: {
            serieProposalItem,
            member: memberEntities[serieProposal.customerPersonId]
          },
          class: 'modal-xl modal-dialog-scrollable'
        })

        modalRef.onHide
          .pipe(take(1), untilDestroyed(this))
          .subscribe((event) => {
            console.log('Modal closed')

            // TODO should refresh data here
          })
      })
  }
}
